
// OAuth analytics tracking system
import { supabase } from '@/lib/supabase';
import { startPerformanceTracking, finishPerformanceTracking } from '@/services/ai-analytics-service';

export interface OAuthMetrics {
  totalAttempts: number;
  successfulAttempts: number;
  failedAttempts: number;
  averageCompletionTime: number;
  errorsByType: Record<string, number>;
  userFlowDropoffs: Record<string, number>;
  retryAttempts: number;
  cacheHitRate: number;
}

export interface OAuthSessionData {
  sessionId: string;
  userId?: string;
  startTime: number;
  endTime?: number;
  success: boolean;
  error?: string;
  retryCount: number;
  steps: OAuthStep[];
  userAgent: string;
  flowType: 'new' | 'retry' | 'refresh';
}

export interface OAuthStep {
  step: 'start' | 'authenticate' | 'fetch_accounts' | 'select_account' | 'complete';
  timestamp: number;
  duration?: number;
  success: boolean;
  error?: string;
  metadata?: Record<string, any>;
}

class OAuthAnalytics {
  private sessions: Map<string, OAuthSessionData> = new Map();
  private metrics: OAuthMetrics = {
    totalAttempts: 0,
    successfulAttempts: 0,
    failedAttempts: 0,
    averageCompletionTime: 0,
    errorsByType: {},
    userFlowDropoffs: {},
    retryAttempts: 0,
    cacheHitRate: 0
  };

  private isDevelopment = import.meta.env.DEV;

  // Start tracking an OAuth session
  startOAuthSession(userId: string, flowType: 'new' | 'retry' | 'refresh' = 'new'): string {
    const sessionId = `oauth_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const sessionData: OAuthSessionData = {
      sessionId,
      userId,
      startTime: Date.now(),
      success: false,
      retryCount: 0,
      steps: [],
      userAgent: navigator.userAgent,
      flowType
    };

    this.sessions.set(sessionId, sessionData);
    this.metrics.totalAttempts++;

    // Start performance tracking
    const { requestId, requestStartTime } = startPerformanceTracking();

    if (this.isDevelopment) {
      console.log('📊 OAuth Session Started:', { sessionId, userId, flowType });
    }

    return sessionId;
  }

  // Track a step in the OAuth flow
  trackOAuthStep(
    sessionId: string, 
    step: OAuthStep['step'], 
    success: boolean, 
    error?: string,
    metadata?: Record<string, any>
  ): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;

    const timestamp = Date.now();
    const previousStep = session.steps[session.steps.length - 1];
    const duration = previousStep ? timestamp - previousStep.timestamp : undefined;

    const stepData: OAuthStep = {
      step,
      timestamp,
      duration,
      success,
      error,
      metadata
    };

    session.steps.push(stepData);

    // Track dropoffs
    if (!success) {
      this.metrics.userFlowDropoffs[step] = (this.metrics.userFlowDropoffs[step] || 0) + 1;
      
      if (error) {
        this.metrics.errorsByType[error] = (this.metrics.errorsByType[error] || 0) + 1;
      }
    }

    if (this.isDevelopment) {
      console.log('📊 OAuth Step:', { sessionId, step, success, duration, error });
    }

    // Send step data to analytics
    this.sendStepAnalytics(sessionId, stepData);
  }

  // Complete an OAuth session
  completeOAuthSession(sessionId: string, success: boolean, error?: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;

    session.endTime = Date.now();
    session.success = success;
    if (error) session.error = error;

    const totalDuration = session.endTime - session.startTime;

    if (success) {
      this.metrics.successfulAttempts++;
      
      // Update average completion time
      const currentAvg = this.metrics.averageCompletionTime;
      const successCount = this.metrics.successfulAttempts;
      this.metrics.averageCompletionTime = 
        (currentAvg * (successCount - 1) + totalDuration) / successCount;
    } else {
      this.metrics.failedAttempts++;
    }

    if (this.isDevelopment) {
      console.log('📊 OAuth Session Completed:', {
        sessionId,
        success,
        duration: totalDuration,
        steps: session.steps.length
      });
    }

    // Send completion analytics
    this.sendCompletionAnalytics(session, totalDuration);
  }

  // Track retry attempt
  trackRetryAttempt(sessionId: string): void {
    const session = this.sessions.get(sessionId);
    if (session) {
      session.retryCount++;
      this.metrics.retryAttempts++;
    }
  }

  // Track cache usage
  trackCacheUsage(hit: boolean): void {
    // Simple cache hit rate calculation
    const totalCacheAttempts = this.metrics.totalAttempts;
    if (totalCacheAttempts > 0) {
      this.metrics.cacheHitRate = hit ? 
        (this.metrics.cacheHitRate * (totalCacheAttempts - 1) + 1) / totalCacheAttempts :
        this.metrics.cacheHitRate * (totalCacheAttempts - 1) / totalCacheAttempts;
    }
  }

  // Get current metrics
  getMetrics(): OAuthMetrics {
    return { ...this.metrics };
  }

  // Get session data
  getSession(sessionId: string): OAuthSessionData | undefined {
    return this.sessions.get(sessionId);
  }

  // Get success rate
  getSuccessRate(): number {
    if (this.metrics.totalAttempts === 0) return 0;
    return (this.metrics.successfulAttempts / this.metrics.totalAttempts) * 100;
  }

  // Get most common errors
  getTopErrors(limit: number = 5): Array<{ error: string; count: number }> {
    return Object.entries(this.metrics.errorsByType)
      .sort(([,a], [,b]) => b - a)
      .slice(0, limit)
      .map(([error, count]) => ({ error, count }));
  }

  // Get flow dropoff points
  getDropoffPoints(): Array<{ step: string; count: number; percentage: number }> {
    const totalDropoffs = Object.values(this.metrics.userFlowDropoffs).reduce((a, b) => a + b, 0);
    
    return Object.entries(this.metrics.userFlowDropoffs)
      .map(([step, count]) => ({
        step,
        count,
        percentage: totalDropoffs > 0 ? (count / totalDropoffs) * 100 : 0
      }))
      .sort((a, b) => b.count - a.count);
  }

  // Send step analytics to database
  private async sendStepAnalytics(sessionId: string, step: OAuthStep): Promise<void> {
    try {
      if (!this.isDevelopment && supabase) {
        await supabase.from('oauth_step_analytics').insert({
          session_id: sessionId,
          step: step.step,
          timestamp: new Date(step.timestamp).toISOString(),
          duration: step.duration,
          success: step.success,
          error: step.error,
          metadata: step.metadata
        });
      }
    } catch (error) {
      // Fail silently to not impact user experience
      if (this.isDevelopment) {
        console.error('Failed to send step analytics:', error);
      }
    }
  }

  // Send completion analytics to database
  private async sendCompletionAnalytics(session: OAuthSessionData, totalDuration: number): Promise<void> {
    try {
      // Send to AI analytics service
      if (session.userId) {
        await finishPerformanceTracking({
          requestId: session.sessionId,
          userId: session.userId,
          operationType: 'oauth_flow',
          requestStartTime: session.startTime,
          processingDuration: totalDuration,
          totalDuration,
          isCached: false,
          topic: 'ctrader_oauth',
          status: session.success ? 'success' : 'error',
          errorMessage: session.error
        });
      }

      // Send to OAuth analytics table
      if (!this.isDevelopment && supabase) {
        await supabase.from('oauth_session_analytics').insert({
          session_id: session.sessionId,
          user_id: session.userId,
          start_time: new Date(session.startTime).toISOString(),
          end_time: session.endTime ? new Date(session.endTime).toISOString() : null,
          total_duration: totalDuration,
          success: session.success,
          error: session.error,
          retry_count: session.retryCount,
          flow_type: session.flowType,
          steps_count: session.steps.length,
          user_agent: session.userAgent
        });
      }
    } catch (error) {
      // Fail silently to not impact user experience
      if (this.isDevelopment) {
        console.error('Failed to send completion analytics:', error);
      }
    }
  }

  // Generate analytics report
  generateReport(timeRange: number = 24 * 60 * 60 * 1000): {
    summary: OAuthMetrics;
    successRate: number;
    topErrors: Array<{ error: string; count: number }>;
    dropoffPoints: Array<{ step: string; count: number; percentage: number }>;
    recentSessions: number;
  } {
    const now = Date.now();
    const recentSessions = Array.from(this.sessions.values())
      .filter(session => now - session.startTime <= timeRange);

    return {
      summary: this.getMetrics(),
      successRate: this.getSuccessRate(),
      topErrors: this.getTopErrors(),
      dropoffPoints: this.getDropoffPoints(),
      recentSessions: recentSessions.length
    };
  }

  // Cleanup old sessions
  cleanup(): void {
    const now = Date.now();
    const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days
    
    for (const [sessionId, session] of this.sessions.entries()) {
      if (now - session.startTime > maxAge) {
        this.sessions.delete(sessionId);
      }
    }
  }
}

// Export singleton instance
export const oauthAnalytics = new OAuthAnalytics();

// Utility functions for tracking common OAuth events
export const startOAuthTracking = (userId: string, flowType: 'new' | 'retry' | 'refresh' = 'new'): string => {
  return oauthAnalytics.startOAuthSession(userId, flowType);
};

export const trackOAuthStart = (sessionId: string) => {
  oauthAnalytics.trackOAuthStep(sessionId, 'start', true);
};

export const trackOAuthAuthentication = (sessionId: string, success: boolean, error?: string) => {
  oauthAnalytics.trackOAuthStep(sessionId, 'authenticate', success, error);
};

export const trackAccountsFetch = (sessionId: string, success: boolean, accountCount?: number, error?: string) => {
  oauthAnalytics.trackOAuthStep(sessionId, 'fetch_accounts', success, error, { accountCount });
};

export const trackAccountSelection = (sessionId: string, success: boolean, selectedAccountId?: string) => {
  oauthAnalytics.trackOAuthStep(sessionId, 'select_account', success, undefined, { selectedAccountId });
};

export const trackOAuthCompletion = (sessionId: string, success: boolean, error?: string) => {
  oauthAnalytics.trackOAuthStep(sessionId, 'complete', success, error);
  oauthAnalytics.completeOAuthSession(sessionId, success, error);
};

export const trackOAuthRetry = (sessionId: string) => {
  oauthAnalytics.trackRetryAttempt(sessionId);
};

export const trackCacheHit = (hit: boolean) => {
  oauthAnalytics.trackCacheUsage(hit);
};

// Initialize cleanup interval
if (typeof window !== 'undefined') {
  setInterval(() => {
    oauthAnalytics.cleanup();
  }, 60 * 60 * 1000); // Run cleanup every hour
}
